<template>
  <div v-if="isOpen" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h2>{{ modalTitle }}</h2>
        <button class="close-button" @click="close">&times;</button>
      </div>

      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <p v-if="mode === 'forgot'" class="form-intro">
            {{ t('Enter your email and we will send you a reset link.') }}
          </p>
          <div v-if="mode === 'register'" class="form-group">
            <label for="displayName">{{ t('Display Name') }}</label>
            <input
              id="displayName"
              v-model="displayName"
              type="text"
              name="displayName"
              autocomplete="name"
              minlength="2"
              maxlength="30"
              required
              :placeholder="t('Your name')"
              :class="{ error: getFieldError('displayName') }"
            />
            <span v-if="getFieldError('displayName')" class="error-message">
              {{ getFieldError('displayName') }}
            </span>
          </div>

          <div class="form-group">
            <label for="email">{{ t('Email') }}</label>
            <input
              id="email"
              v-model="email"
              type="email"
              name="email"
              autocomplete="email"
              maxlength="254"
              required
              :placeholder="t('you@example.com')"
              :class="{ error: getFieldError('email') }"
            />
            <span v-if="getFieldError('email')" class="error-message">
              {{ getFieldError('email') }}
            </span>
          </div>

          <div v-if="mode !== 'forgot'" class="form-group">
            <label for="password">{{ t('Password') }}</label>
            <input
              id="password"
              v-model="password"
              type="password"
              name="password"
              :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
              minlength="8"
              maxlength="128"
              required
              :placeholder="t('Min 8 characters, letter + number')"
              :class="{ error: getFieldError('password') }"
            />
            <span v-if="getFieldError('password')" class="error-message">
              {{ getFieldError('password') }}
            </span>
          </div>

          <div v-if="mode === 'register'" class="form-group consent-group">
            <label class="checkbox-row">
              <input
                v-model="subscribeNewsletter"
                type="checkbox"
                name="subscribeNewsletter"
              />
              <span class="checkbox-text">
                {{ t('Send me occasional news and updates by email') }}
              </span>
            </label>
          </div>

          <div v-if="forgotMessage" class="success-banner">
            <strong>{{ t('Check your email') }}</strong>
            <span>{{ t('If an account exists for that email, a password reset link has been sent.') }}</span>
          </div>

          <div v-if="error" class="error-banner">
            {{ error }}
          </div>

          <button type="submit" class="submit-button" :disabled="loading">
            {{ submitLabel }}
          </button>
        </form>

        <div class="mode-switch">
          <span v-if="mode === 'login'">
            {{ t("Don't have an account?") }}
            <a href="#" @click.prevent="setMode('register')">{{ t('Sign up') }}</a>
            <a class="forgot-link" href="#" @click.prevent="setMode('forgot')">{{ t('Forgot password?') }}</a>
          </span>
          <span v-else-if="mode === 'register'">
            {{ t('Already have an account?') }}
            <a href="#" @click.prevent="setMode('login')">{{ t('Log in') }}</a>
          </span>
          <span v-else>
            <a href="#" @click.prevent="setMode('login')">{{ t('Back to log in') }}</a>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useAuth } from '../composables/useAuth.js';
import { useI18n } from '../composables/useI18n.js';

const { t } = useI18n();

const props = defineProps<{
  isOpen: boolean;
  initialMode?: 'login' | 'register';
}>();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const { login, register, requestPasswordReset, loading, error, validationErrors, clearError } = useAuth();

type AuthMode = 'login' | 'register' | 'forgot';
const mode = ref<AuthMode>(props.initialMode || 'login');
const email = ref('');
const password = ref('');
const displayName = ref('');
const subscribeNewsletter = ref(false);
const forgotMessage = ref('');

const modalTitle = computed(() => {
  if (mode.value === 'login') return t('Log In');
  if (mode.value === 'register') return t('Sign Up');
  return t('Reset your password');
});

const submitLabel = computed(() => {
  if (loading.value) return t('Please wait...');
  if (mode.value === 'login') return t('Log In');
  if (mode.value === 'register') return t('Sign Up');
  return t('Send Reset Link');
});

function getFieldError(field: string): string | undefined {
  return validationErrors.value.find(e => e.field === field)?.message;
}

function setMode(nextMode: AuthMode) {
  mode.value = nextMode;
  forgotMessage.value = '';
  subscribeNewsletter.value = false;
  clearError();
}

function close() {
  emit('close');
  clearError();
  email.value = '';
  password.value = '';
  displayName.value = '';
  subscribeNewsletter.value = false;
  forgotMessage.value = '';
}

function handleOverlayClick() {
  close();
}

async function handleSubmit() {
  try {
    if (mode.value === 'forgot') {
      await requestPasswordReset(email.value);
      forgotMessage.value = 'sent';
      return;
    } else if (mode.value === 'login') {
      await login(email.value, password.value);
    } else {
      await register(
        email.value,
        password.value,
        displayName.value,
        subscribeNewsletter.value,
      );
    }
    emit('success');
    close();
  } catch (err) {
    // Error is already set in useAuth
  }
}

// Reset mode when modal opens
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    mode.value = props.initialMode || 'login';
    clearError();
    forgotMessage.value = '';
    subscribeNewsletter.value = false;
  }
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--bg-raised);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border);
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-strong);
}

.close-button {
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  color: var(--text-muted);
  line-height: 1;
  padding: 0;
  width: 30px;
  height: 30px;
}

.close-button:hover {
  color: var(--text);
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-intro {
  margin: 0 0 16px;
  color: var(--text-muted);
  line-height: 1.5;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: var(--text);
}

.form-group input {
  width: 100%;
  padding: 10px;
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 1rem;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: var(--text-muted);
}

.form-group input.error {
  border-color: var(--danger-border);
}

.form-group input:focus {
  outline: none;
  border-color: var(--accent);
}

.error-message {
  display: block;
  color: var(--danger-text);
  font-size: 0.875rem;
  margin-top: 4px;
}

.consent-group {
  display: grid;
  gap: 10px;
}

.checkbox-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}

.checkbox-row input[type="checkbox"] {
  width: auto;
  margin: 2px 0 0;
  flex-shrink: 0;
  cursor: pointer;
}

.checkbox-row input[type="checkbox"].error {
  outline: 2px solid var(--danger-border);
  outline-offset: 2px;
}

.checkbox-text {
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--text);
}

.inline-link {
  color: var(--link);
  text-decoration: underline;
}

.required-mark {
  color: var(--danger-text);
  margin-left: 2px;
  font-weight: 700;
  cursor: help;
}

.error-banner {
  background: var(--danger-bg);
  color: var(--danger-text);
  padding: 12px;
  border-radius: var(--radius-sm);
  margin-bottom: 16px;
  border: 1px solid var(--danger-border);
}

.success-banner {
  display: grid;
  gap: 4px;
  background: color-mix(in srgb, var(--green) 15%, var(--surface));
  color: var(--text);
  padding: 12px;
  border-radius: var(--radius-sm);
  margin-bottom: 16px;
  border: 1px solid var(--green);
}

.submit-button {
  width: 100%;
  padding: 12px;
  background: var(--green);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.submit-button:hover:not(:disabled) {
  background: var(--green-hover);
}

.submit-button:disabled {
  background: #3a4150;
  color: var(--text-muted);
  cursor: not-allowed;
}

.mode-switch {
  text-align: center;
  margin-top: 16px;
  color: var(--text-muted);
}

.mode-switch a {
  color: var(--link);
  text-decoration: none;
  font-weight: 500;
}

.mode-switch a:hover {
  text-decoration: underline;
}

.forgot-link {
  display: block;
  margin-top: 10px;
}
</style>
